import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bookmark, Users } from 'lucide-react';

import { useMainButton } from '@/hooks/useMainButton';
import { useMyAccountsQuery } from '@/services/qriotsApi';

interface RiotSearchFormProps {
  initialGameName?: string;
  initialTagLine?: string;
  loading?: boolean;
  onSubmit: (riotId: { gameName: string; tagLine: string }) => void;
}

const TAGLINE_RE = /^[A-Za-z0-9]{3,5}$/;

export function RiotSearchForm({
  initialGameName = '',
  initialTagLine = '',
  loading = false,
  onSubmit,
}: RiotSearchFormProps) {
  const { t } = useTranslation('riot');
  const [gameName, setGameName] = useState(initialGameName);
  const [tagLine, setTagLine] = useState(initialTagLine);
  const myAccounts = useMyAccountsQuery();
  const savedCount = myAccounts.data?.accounts.length ?? 0;

  // Allow paste of "Foo#BAR1" into gameName and auto-split.
  const onGameNameChange = (raw: string) => {
    if (raw.includes('#')) {
      const [gn, tl] = raw.split('#');
      setGameName((gn ?? '').slice(0, 16));
      setTagLine((tl ?? '').slice(0, 5));
    } else {
      setGameName(raw.slice(0, 16));
    }
  };

  const taglineValid = TAGLINE_RE.test(tagLine.trim());
  const formValid = gameName.trim().length > 0 && taglineValid;

  useMainButton({
    text: t('cta.search'),
    enabled: formValid && !loading,
    loading,
    onClick: () => onSubmit({ gameName: gameName.trim(), tagLine: tagLine.trim() }),
  });

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-fg">{t('title.lookup')}</h1>
        <p className="text-sm text-fg-muted">{t('title.subtitle')}</p>
      </header>

      <div className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-border bg-bg-secondary focus-within:border-brand">
        <input
          type="text"
          value={gameName}
          onChange={(e) => onGameNameChange(e.target.value)}
          placeholder={t('field.gameName')}
          maxLength={16}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
        />
        <span aria-hidden className="flex select-none items-center px-1 text-sm font-medium text-fg-muted">#</span>
        <input
          type="text"
          value={tagLine}
          onChange={(e) => setTagLine(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 5))}
          placeholder={t('field.tagLine')}
          maxLength={5}
          className="w-24 bg-transparent px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
        />
      </div>

      {tagLine.length > 0 && !taglineValid && (
        <p className="text-xs text-danger">{t('field.error.taglineFormat')}</p>
      )}

      <p className="text-xs text-fg-muted">{t('field.hint.autoRegion')}</p>

      {savedCount > 0 && (
        <Link
          to="/riot/saved"
          className="mt-2 flex items-center justify-between rounded-lg border border-border bg-bg-tertiary px-3 py-2.5 text-sm font-medium text-fg active:opacity-80"
        >
          <span className="flex items-center gap-2">
            <Bookmark aria-hidden className="h-4 w-4 text-success" />
            {String(t('saved.cta', { count: savedCount }))}
          </span>
          <span aria-hidden className="text-fg-muted">→</span>
        </Link>
      )}

      <Link
        to="/riot/multi-search"
        className="mt-2 flex items-center justify-between rounded-lg border border-border bg-bg-tertiary px-3 py-2.5 text-sm font-medium text-fg active:opacity-80"
      >
        <span className="flex items-center gap-2">
          <Users aria-hidden className="h-4 w-4 text-brand" />
          {t('multiSearch.entryPoint')}
        </span>
        <span aria-hidden className="text-fg-muted">→</span>
      </Link>
    </div>
  );
}
